import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { FormFieldsPanel } from "../../../components/form-fields-panel/form-fields-panel";
import { LabelValue } from "../../../components/LabelValue/LabelValue";
import { Loader } from "../../../components/Loader/Loader";
import { SectionHeader } from "../../../components/SectionHeader/SectionHeader";
import {
  Employee,
  EmployeeRoles,
  EmployeeRolesMapping,
  EmployeeStatuses,
  EmployeeStatusLabels,
} from "../../../models/Employee";
import { requests } from "../../../services/http-common";
import { useToast } from "../../../Providers/ToastProvider";
import classes from "./EmployeeDetails.module.scss";
import { classNames as cx } from "primereact/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useKeycloakContext } from "../../../keycloak";

const formatDate = (date: Date) => new Date(date).toLocaleDateString();

const type = {
  [EmployeeStatuses.ACTIVE]: "active",
  [EmployeeStatuses.TERMINATED_REHIRABLE]: "terminated-rehirable",
  [EmployeeStatuses.TERMINATED]: "terminated",
};

type EmployeeDetailsProps = {
  isOwn?: boolean;
};

export const EmployeeDetails = ({ isOwn = false }: EmployeeDetailsProps) => {
  let { employeeId } = useParams<{ employeeId: string }>();

  const keycloakContexxt = useKeycloakContext();
  const subId = keycloakContexxt.keycloak?.tokenParsed?.sub;

  if (isOwn) {
    employeeId = subId;
  }

  const navigate = useNavigate();
  const { show } = useToast();

  const sendWelcome = useMutation({
    mutationFn: () => {
      return requests.post(`/user/${employeeId}/send-welcome-email`, {});
    },
    onSuccess: () => {
      show({ detail: "Welcome email was sent successfully !" });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const { data, isLoading } = useQuery<Employee>({
    queryKey: [`employee-form-${employeeId}`],
    queryFn: () => {
      return requests.get(`/user/${employeeId}`);
    },
  });

  if (isLoading || !data) {
    return <Loader />;
  }

  return (
    <div className={classes.container}>
      <SectionHeader
        title={isOwn ? "Profile" : "Employee profile"}
        showBackButton={!isOwn}
      />
      <div className={classes.actionBar}>
        <Button
          label="Edit"
          className="p-button-outlined p-button-secondary"
          onClick={() =>
            navigate(
              isOwn
                ? "/profile/edit"
                : `/management/employees/${employeeId}/details/edit`
            )
          }
        />
        {!isOwn && (
          <Button
            label="Send welcome email"
            className="p-button-outlined p-button-secondary"
            loading={sendWelcome.isLoading}
            onClick={() => sendWelcome.mutate()}
          />
        )}
      </div>
      <div className={classes.content}>
        <FormFieldsPanel
          label="Employee details"
          className={classes.employeeAccount}
        >
          <div className={classes.group}>
            <LabelValue bold displayInline label="First Name">
              {data.firstName}
            </LabelValue>
            <LabelValue bold displayInline label="Status">
              <div className={cx(classes.status, classes[type[data?.status]])}>
                {EmployeeStatusLabels[data.status]}
              </div>
            </LabelValue>
            <LabelValue bold displayInline label="Last Name">
              {data?.lastName}
            </LabelValue>
            <LabelValue bold displayInline label="Social security Number">
              {data?.ssn}
            </LabelValue>
            <LabelValue bold displayInline label="Termination Date">
              {data?.terminationDate
                ? `${formatDate(data.terminationDate)}`
                : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Hiring date">
              {data?.hireDate ? `${formatDate(data.hireDate)}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Position">
              {EmployeeRolesMapping[data?.role as EmployeeRoles]}
            </LabelValue>
          </div>
        </FormFieldsPanel>
        <FormFieldsPanel label="General information">
          <div className={classes.group}>
            <LabelValue bold displayInline label="Birthdate">
              {data?.birthDate ? `${formatDate(data?.birthDate)}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Home Phone">
              {data?.homePhone ? `${data?.homePhone}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="DL N">
              {data?.dlStateN || "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Work Phone">
              {data?.workPhone ? `${data?.workPhone}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Hire Date">
              {data?.hireDate ? `${formatDate(data?.hireDate)}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Emergency Contact Number">
              {data?.emergencyContact ? `${data?.emergencyContact}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Email Address">
              {data?.email}
            </LabelValue>
            <LabelValue bold displayInline label="Emergency Contact Name">
              {data?.emergencyContactName
                ? `${data?.emergencyContactName}`
                : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Desired Hours per week">
              {data?.desiredHoursPerWeek ? `${data?.desiredHoursPerWeek}` : "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Address Line 1">
              {data?.addressLine1}
            </LabelValue>
            <LabelValue
              bold
              displayInline
              label="Pay Rate"
            >{`$ ${data?.payRate}`}</LabelValue>
            <LabelValue bold displayInline label="Address Line 2">
              {data?.addressLine2}
            </LabelValue>
            <LabelValue
              bold
              displayInline
              label="Overtime Rate"
            >{`$ ${data?.overTimeRate}`}</LabelValue>
            <LabelValue bold displayInline label="City">
              {data?.city}
            </LabelValue>
            <LabelValue bold displayInline label="Supervisor Alerts">
              {data?.supervisorAlerts}
            </LabelValue>
            <LabelValue bold displayInline label="State">
              {data?.state}
            </LabelValue>
            <div></div>
            <LabelValue bold displayInline label="ZIP">
              {data?.ZIP || "-"}
            </LabelValue>
          </div>
        </FormFieldsPanel>
        <FormFieldsPanel label="Banking information">
          <div className={classes.groupVertical}>
            <LabelValue bold displayInline label="Account Name">
              {data?.accountName || "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Routing Number">
              {data?.routingNumber || "-"}
            </LabelValue>
            <LabelValue bold displayInline label="Account Number">
              {data?.accountNumber || "-"}
            </LabelValue>
          </div>
        </FormFieldsPanel>
        <FormFieldsPanel label="Additional information">
          <div className={classes.groupVertical}>
            <LabelValue bold displayInline label="Notes">
              {data?.notes || "-"}
            </LabelValue>
            <LabelValue>
              <div className={classes.checkbox}>
                <Checkbox checked={data?.mobileAccess} disabled />
                <label htmlFor={"mobileAccess"}>Mobile Access</label>
              </div>
            </LabelValue>
            <LabelValue
              bold
              displayInline
              label="PDF attachment background screening"
            >
              <a
                href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${data?.pdfAttachmentsArr?.[0]}`}
                download
                target="_blank"
                rel="noreferrer"
              >
                {data?.pdfAttachmentsArr?.[0]?.slice(
                  data?.pdfAttachmentsArr?.[0].lastIndexOf("/") + 1
                ) || "-"}
              </a>
            </LabelValue>
            <div className={classes.docList}>
              <h4>Other Documents</h4>
              <DataTable value={data?.otherDocumentsArr || []}>
                <Column
                  header="Name"
                  body={(rowData) => (
                    <a
                      href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${rowData}`}
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      {rowData.slice(rowData.lastIndexOf("/") + 1) || "-"}
                    </a>
                  )}
                ></Column>
              </DataTable>
            </div>
          </div>
        </FormFieldsPanel>
      </div>
    </div>
  );
};
